// Issue detail page — resolution workflow with KB save, export, and user feedback
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/layout/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ArrowLeft, Send, Loader2, AlertTriangle, BookOpen, MessageSquare } from 'lucide-react'
import { useIssueDetail } from '@/hooks/useIssueDetail'
import { useResolutionPipeline } from '@/hooks/useResolutionPipeline'
import { IssueConversationList } from '@/components/issues/IssueConversationList'
import { IssueDetailChat } from '@/components/issues/IssueDetailChat'
import { IssueMetadataCard } from '@/components/issues/IssueMetadataCard'
import { IssueAttachmentsList } from '@/components/issues/IssueAttachmentsList'
import { IssueStatusActions } from '@/components/issues/IssueStatusActions'
import { ExpertAssignmentPanel } from '@/components/issues/ExpertAssignmentPanel'
import { ResolutionTimeline } from '@/components/resolution/ResolutionTimeline'
import { ResolutionFeedbackPanel } from '@/components/resolution/ResolutionFeedbackPanel'
import { SaveToKBDialog } from '@/components/resolution/SaveToKBDialog'
import { ExportInstructions } from '@/components/resolution/ExportInstructions'
import { ApprovalStatus } from '@/components/resolution/ApprovalStatus'
import { exportAsPDF, exportAsDOCX } from '@/lib/export-instructions'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { ResolutionTierName } from '@/types/issue'

export function IssueDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const {
    issue, customer, entity, reportedBy,
    conversations, attachments, assignments,
    isLoading, error, addComment, isAddingComment, invalidateAll,
  } = useIssueDetail(id)
  const pipeline = useResolutionPipeline()
  const [comment, setComment] = useState('')
  const [showChat, setShowChat] = useState(false)
  const [pipelineStarted, setPipelineStarted] = useState(false)
  const [showSaveKB, setShowSaveKB] = useState(false)
  const [savedToKB, setSavedToKB] = useState(false)

  const issueData = issue as Record<string, unknown> | undefined

  useEffect(() => {
    if (!issueData || pipelineStarted || pipeline.isRunning) return
    const aiAnalysis = issueData.ai_analysis as Record<string, unknown> | undefined
    const pipelineStatus = aiAnalysis?.resolutionPipelineStatus as string | undefined

    // Don't re-run pipeline if already completed or awaiting feedback
    if (pipelineStatus === 'completed' || pipelineStatus === 'awaiting_user_feedback') {
      if (aiAnalysis?.cachedInstructions) {
        setPipelineStarted(true)
        // Pass full ai_analysis so restoreFromCache can find _acceptedTier + cached tiers
        pipeline.restoreFromCache(aiAnalysis as Record<string, unknown>)
      }
      return
    }

    const autoResolve = searchParams.get('autoResolve') === 'true'
    const pendingPipeline = pipelineStatus === 'pending'

    if ((autoResolve || pendingPipeline) && id) {
      setPipelineStarted(true)
      // Remove autoResolve from URL so it doesn't re-trigger on navigation
      if (autoResolve) {
        const newParams = new URLSearchParams(searchParams)
        newParams.delete('autoResolve')
        window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`)
      }
      pipeline.startPipeline({
        issueId: id,
        description: String(issueData.description ?? ''),
        d365Version: String(issueData.d365_version ?? ''),
        d365Module: String(issueData.d365_module ?? ''),
        expertiseDomain: String(issueData.expertise_domain ?? ''),
      })
    }
  }, [issueData, id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddComment = async () => {
    if (!comment.trim()) return
    try { await addComment(comment.trim()); setComment('') } catch (err) { console.error('Failed:', err) }
  }

  const handleStartPipeline = () => {
    if (!issueData || !id) return
    setPipelineStarted(true)
    pipeline.startPipeline({
      issueId: id, description: String(issueData.description ?? ''),
      d365Version: String(issueData.d365_version ?? ''),
      d365Module: String(issueData.d365_module ?? ''),
      expertiseDomain: String(issueData.expertise_domain ?? ''),
    })
  }

  const handleAcceptSolution = async (tier: ResolutionTierName) => {
    pipeline.acceptSolution(tier)
    if (id) {
      const existing = (issueData?.ai_analysis as Record<string, unknown>) || {}
      const newStatus = tier === 'expert' ? 'in_progress' : 'resolved'
      await supabase.from('issues').update({
        status: newStatus,
        ai_analysis: { ...existing, resolutionPipelineStatus: 'completed', resolvedByTier: tier, _acceptedTier: tier },
        ...(newStatus === 'resolved' ? { resolved_at: new Date().toISOString() } : {}),
      } as never).eq('id', id)
    }
    if (tier !== 'kb') setShowSaveKB(true)
  }

  const handleSendToUser = async (tier: ResolutionTierName) => {
    if (!id) return
    const existing = (issueData?.ai_analysis as Record<string, unknown>) || {}
    await supabase.from('issues').update({
      status: 'waiting_info',
      ai_analysis: { ...existing, resolutionPipelineStatus: 'awaiting_user_feedback', sentTier: tier },
    } as never).eq('id', id)
    handleExportPDF(tier)
  }

  const buildExportData = (tier: ResolutionTierName) => {
    const tierData = pipeline.tiers.find((t) => t.tier === tier)
    const instr = tierData?.generatedInstructions
    return {
      issueTitle: String(issueData?.title ?? ''),
      issueDescription: String(issueData?.description ?? ''),
      d365Version: String(issueData?.d365_version ?? ''),
      d365Module: String(issueData?.d365_module ?? ''),
      category: String(issueData?.category ?? ''),
      customerName: String(issueData?.customer_name ?? ''),
      resolutionSummary: instr?.summary ?? '',
      steps: instr?.steps ?? [],
      exportedBy: user?.email || 'System',
      exportDate: new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }),
    }
  }

  const handleExportPDF = (tier: ResolutionTierName) => exportAsPDF(buildExportData(tier))
  const handleExportDOCX = (tier: ResolutionTierName) => exportAsDOCX(buildExportData(tier))

  if (isLoading) {
    return <div className="flex h-[50vh] items-center justify-center"><LoadingSpinner message="Loading issue..." /></div>
  }
  if (error || !issueData) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertDescription>{error?.message ?? 'Issue not found.'}</AlertDescription></Alert>
        <Button variant="outline" onClick={() => navigate('/issues')}><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
      </div>
    )
  }

  const issueStatus = String(issueData.status)
  const isResolved = issueStatus === 'resolved' || pipeline.acceptedTier !== null
  const aiAnalysisData = issueData.ai_analysis as Record<string, unknown> | undefined
  const pipelineStatus = aiAnalysisData?.resolutionPipelineStatus as string | undefined
  const isAwaitingFeedback = issueStatus === 'waiting_info'
    && pipelineStatus === 'awaiting_user_feedback'
  const canReopenResolution = isResolved && issueStatus !== 'closed'
  const resolutionSummary = String(issueData.resolution_summary ?? issueData.description ?? '')

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col gap-3">
      <div className="flex items-start justify-between shrink-0">
        <div>
          <Link to="/issues" className="mb-1 inline-flex items-center gap-1 text-sm text-kaycoo-charcoal-2 hover:text-kaycoo-teal-dark">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Issues
          </Link>
          <h1 className="text-xl font-bold tracking-tight text-kaycoo-teal-dark">{String(issueData.title ?? 'Untitled')}</h1>
          <p className="mt-0.5 text-sm text-kaycoo-charcoal-2 line-clamp-1">{String(issueData.description ?? '')}</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {isResolved && (
            <>
              <ExportInstructions
                issueTitle={String(issueData.title ?? '')} issueDescription={String(issueData.description ?? '')}
                d365Version={String(issueData.d365_version ?? '')} d365Module={String(issueData.d365_module ?? '')}
                category={String(issueData.category ?? '')} customerName={String(issueData.customer_name ?? '')}
                resolutionSummary={resolutionSummary}
              />
              {!savedToKB && (
                <Button variant="outline" size="sm" onClick={() => setShowSaveKB(true)}
                  className="gap-1.5 border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale">
                  <BookOpen className="h-3.5 w-3.5" /> Save to KB
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Compact metadata bar — collapsible */}
      <details className="shrink-0 rounded-lg border border-kaycoo-white-3 bg-kaycoo-white px-3 py-2">
        <summary className="cursor-pointer text-xs font-medium text-kaycoo-charcoal-2">
          {String(issueData.d365_version ?? '')} | {String(issueData.d365_module ?? '')} | {String(issueData.category ?? '')} | {String(issueData.priority ?? '')}
          <span className="ml-2">(details)</span>
        </summary>
        <div className="mt-3">
          <IssueMetadataCard issue={issueData} />
        </div>
      </details>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-5">
        {/* Left: Conversation + Chat (3/5) */}
        <div className="flex flex-col gap-3 overflow-y-auto lg:col-span-3">
          <IssueConversationList conversations={conversations} />

          {(isAwaitingFeedback || canReopenResolution) && id && (
            <ResolutionFeedbackPanel
              issueId={id} organizationId={String(issueData.organization_id ?? '')}
              isResolved={canReopenResolution}
              onResolved={() => { setShowSaveKB(true); invalidateAll() }}
              onRetry={() => { setPipelineStarted(false); pipeline.reset(); invalidateAll() }}
              onContinueChat={() => setShowChat(true)}
            />
          )}

          {showChat && id ? (
            <IssueDetailChat
              issueId={id}
              issueDescription={String(issueData.description ?? '')}
              d365Version={String(issueData.d365_version ?? '')}
              d365Module={String(issueData.d365_module ?? '')}
              existingConversations={conversations.map((c: Record<string, unknown>) => ({
                role: String(c.role ?? 'user'), content: String(c.content ?? ''),
              }))}
              customer={customer as never}
              entity={entity as never}
              reportedBy={reportedBy as never}
              organizationId={String(issueData.organization_id ?? '')}
              onClose={() => setShowChat(false)}
            />
          ) : (
            <Card className="border-kaycoo-teal-light bg-kaycoo-white p-3">
              <div className="flex gap-2">
                <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment..." className="border-kaycoo-white-3"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment() } }} />
                <Button onClick={handleAddComment} disabled={!comment.trim() || isAddingComment} size="icon"
                  className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
                  {isAddingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={() => setShowChat(true)}
                  className="border-kaycoo-teal text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale"
                  title="Continue AI Chat">
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          )}
        </div>

        {/* Right: Resolution Pipeline + Actions (2/5) */}
        <div className="space-y-4 overflow-y-auto lg:col-span-2">
          <IssueStatusActions issueId={id!} currentStatus={String(issueData.status ?? 'open')} />

          {String(issueData.status) !== 'closed' && (
            <ResolutionTimeline
              tiers={pipeline.tiers} isRunning={pipeline.isRunning}
              pausedAtTier={pipeline.pausedAtTier} acceptedTier={pipeline.acceptedTier}
              onStartPipeline={!pipelineStarted ? handleStartPipeline : undefined}
              onAcceptSolution={handleAcceptSolution} onRejectAndContinue={pipeline.rejectAndContinue}
              onSkipToExpert={pipeline.skipToExpert} onSendToUser={handleSendToUser}
              onExportPDF={handleExportPDF} onExportDOCX={handleExportDOCX}
            />
          )}

          {showSaveKB && id && (() => {
            const acceptedTierData = pipeline.acceptedTier
              ? pipeline.tiers.find((t) => t.tier === pipeline.acceptedTier)
              : null
            const cachedInstructions = acceptedTierData?.generatedInstructions
            return (
              <SaveToKBDialog issueId={id} issueTitle={String(issueData.title ?? '')}
                issueDescription={String(issueData.description ?? '')} d365Version={String(issueData.d365_version ?? '')}
                d365Module={String(issueData.d365_module ?? '')} category={String(issueData.category ?? 'other')}
                resolutionSummary={cachedInstructions?.summary}
                resolutionSteps={cachedInstructions?.steps}
                onSaved={() => { setShowSaveKB(false); setSavedToKB(true) }} onClose={() => setShowSaveKB(false)} />
            )
          })()}

          <IssueAttachmentsList attachments={attachments as unknown as { id: string; file_name: string; file_type: string; file_size_bytes: number; storage_path: string; storage_bucket: string; created_at: string }[]} />
          <ApprovalStatus issueId={id!} />
          <ExpertAssignmentPanel issueId={id!} assignments={assignments as unknown as { id: string; status: string; assigned_at: string; expert_profiles?: { full_name: string; email: string } | null }[]} />
        </div>
      </div>
    </div>
  )
}
